/**
 * Created by Ning Jiang on 3/22/2017.
 */

import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {GameModeEvent} from "@nolimitcity/slot-game/bin/core/gamemode/event/GameModeEvent";
import {GameMode} from "@nolimitcity/slot-game/bin/core/gamemode/GameMode";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {ScreenEvent} from "@nolimitcity/slot-game/bin/core/screen/event/ScreenEvent";
import {ParsedInitData} from "@nolimitcity/slot-game/bin/core/server/data/ParsedInitData";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {SpinEvent} from "@nolimitcity/slot-game/bin/core/spin/event/SpinEvent";
import {WinPresentationController} from "@nolimitcity/slot-game/bin/core/winpresentation/WinPresentationController";
import {TimelineLite} from "gsap";
import {WixxReelSymbol} from "../../reelsymbol/WixxReelSymbol";
import {WixxParsedGameData} from "../../server/data/WixxParsedGameData";
import {WixxHelper} from "../../utils/WixxHelper";
import {Wixx} from "../../Wixx";
import {WixxGameConfig} from "../../WixxGameConfig";
import {WixxSoundConfig} from "../../WixxSoundConfig";

export class BoosterWinPresentationController extends WinPresentationController {
    private readonly MULTIPLIER_DELAY = 0.4; // 12 frames.

    private _reelDuration:number = 0.3;
    private _otherMultiplierFadeInDuration:number = 0.5;
    private _reelsNum:number;
    private _symbolsNum:number;

    private _nextFeature:string = "BASE";
    private _featureSymbolNames:string[];
    private _timeline:TimelineLite | null;
    private _winSymbols:StateReelSymbol[] = [];
    private _multiplierSymbols:StateReelSymbol[] = [];

    constructor(indexes:[number, number]) {
        super(indexes, {name : "BoosterWP", skippable : false});

        this._reelsNum = GameConfig.instance.REELS_NUM;
        this._symbolsNum = GameConfig.instance.SYMBOLS_NUM_IN_REEL[2];

        this.addTweakModuleSlider({
            text : "Reel Duration",
            minValue : 0.1,
            maxValue : 1.5,
            startValue : this._reelDuration,
            onValueChangeCallback : (text:string, newValue:number) => this.onSymbolDurationChanged(newValue)
        });

        this.addTweakModuleSlider({
            text : "Other Multiplier Fade in Duration",
            minValue : 0.1,
            maxValue : 1.5,
            startValue : this._otherMultiplierFadeInDuration,
            onValueChangeCallback : (text:string, newValue:number) => this.onOtherMultiplierFadeInDurationChanged(newValue)
        });
    }

    private onSymbolDurationChanged(newValue:number):void {
        this._reelDuration = newValue;
    }

    private onOtherMultiplierFadeInDurationChanged(newValue:number):void {
        this._otherMultiplierFadeInDuration = newValue;
    }

    protected addFeatureEventHandlers():void {
        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event:GameEvent) => this.onInitDataParsed(event.params[0]));
        EventHandler.addEventListener(this, ScreenEvent.GAME_RESTORE, (event:GameEvent) => this.onGameRestore());
        EventHandler.addEventListener(this, SpinEvent.STOPPED, (event:GameEvent) => this.onSpinStopped());
        EventHandler.addEventListener(this, SpinEvent.FINISH, (event:GameEvent) => this.onFinish());
        EventHandler.addEventListener(this, SpinEvent.BEFORE_START, (event:GameEvent) => this.onFinish()); // for abort wp so there is no FINISH event.
    }

    private onInitDataParsed(data:ParsedInitData):void {
        this._nextFeature = this.getNextFeature(data.nextReelSetName);
    }

    private getNextFeature(nextReelSetName:string):string {
        return nextReelSetName.substr(0, nextReelSetName.length - 8);
    }

    protected parseFeatureGameData(data:WixxParsedGameData):void {
        this._nextFeature = this.getNextFeature(data.nextReelSetName);
        this._featureSymbolNames = WixxHelper.featureToSymbols(this._nextFeature);
    }

    private onGameRestore():void {
        EventHandler.dispatchEvent(new GameEvent(GameModeEvent.CHANGE_MODE, {
            newMode : GameMode.RESPIN,
            feature : this._nextFeature,
            updateBoostedSymbols : true
        }));
    }

    private onFinish():void {
        EventHandler.dispatchEvent(new GameEvent(GameModeEvent.CHANGE_MODE, {
            newMode : GameMode.NORMAL,
            feature : this._nextFeature
        }));
    }

    protected hasWin():boolean {
        return (this._gameData != null) && (this._gameData.nextMode === GameMode.RESPIN);
    }

    private onSpinStopped():void {
        if(!this.hasWin()) {
            return;
        }

        // duplicated code from startWP.
        for(let i:number = 0; i < this._reelsNum; i++) {
            const showMultiplier:boolean = this._featureSymbolNames.indexOf("M2") > -1;
            const isRedFeature:boolean = this._featureSymbolNames.indexOf("M1") > -1;

            const reelSymbols:(StateReelSymbol | null)[] = Reels.allSymbols(i);
            reelSymbols.forEach((symbol:StateReelSymbol | null, symbolIndex:number) => {
                if(!symbol) {
                    return;
                }

                if(this._featureSymbolNames.indexOf(symbol.symName) > -1) {
                    this._winSymbols.push(symbol);
                }

                if(!showMultiplier) {
                    return;
                }

                if(!WixxHelper.isMidSymbol(symbol.symName)) {
                    return;
                }

                // https://github.com/nolimitcity/wixx/issues/54
                if(isRedFeature && symbol.symName === "M1") {
                    return;
                }

                this._multiplierSymbols.push(symbol);
            });
        }
    }

    protected startWinPresentation():void {
        EventHandler.dispatchEvent(new GameEvent(GameModeEvent.CHANGE_MODE, {
            newMode : GameMode.RESPIN,
            feature : this._nextFeature
        }));

        const timeline:TimelineLite = new TimelineLite({paused : true});
        const otherMultiplierTimeline:TimelineLite = new TimelineLite();

        let symbol:StateReelSymbol | null;
        let featurePerReel:number[] = [];
        const showMultiplier:boolean = this._featureSymbolNames.indexOf("M2") > -1;
        const isRedFeature:boolean = this._featureSymbolNames.indexOf("M1") > -1;

        timeline.add(() => {
            if(WixxSoundConfig.instance.MAIN_GAME_AMBIANCE != null) {
                SlotGame.sound.fadeAmbience(0.0, 100);
            }
        }, 0);

        for(let i:number = 0; i < this._reelsNum; i++) {
            featurePerReel = [];
            const reelSymbols:(StateReelSymbol | null)[] = Reels.allSymbols(i);
            for(let j:number = 0; j < reelSymbols.length; j++) {
                symbol = reelSymbols[j];
                if(!symbol) {
                    continue;
                }
                const featureIndex:number = this._featureSymbolNames.indexOf(symbol.symName);
                if(featureIndex > -1) {
                    timeline.add(symbol.changeState({
                        state : WixxGameConfig.instance.SYMBOL_STATES.boost,
                        fadeInDelay : this._reelDuration * (featureIndex * this._reelsNum + i)
                    }), 0);

                    if(featurePerReel.indexOf(featureIndex) < 0) {
                        timeline.add(() => {
                            if(WixxSoundConfig.instance.SYMBOL_TRANSFORM_UP != null) {
                                SlotGame.sound.playEffect(WixxSoundConfig.instance.SYMBOL_TRANSFORM_UP);
                            }
                        }, (this._reelDuration * (featureIndex * this._reelsNum + i) + 0.5).toString());
                    }
                    featurePerReel.push(featureIndex);
                }

                if(!showMultiplier) {
                    continue;
                }

                if(!WixxHelper.isMidSymbol(symbol.symName)) {
                    continue;
                }

                // https://github.com/nolimitcity/wixx/issues/54
                if(isRedFeature && symbol.symName === "M1") {
                    continue;
                }

                if(symbol.symName === "M2") {
                    timeline.add((<WixxReelSymbol>symbol).playMultiplier(), this._reelDuration * (featureIndex * this._reelsNum + i) + this.MULTIPLIER_DELAY);
                } else {
                    // Show X5 on all other mid symbols.
                    otherMultiplierTimeline.add((<WixxReelSymbol>symbol).playMultiplier(symbol.currentState!.keyword === WixxGameConfig.instance.SYMBOL_STATES.noWin!.keyword ? WixxGameConfig.instance.SYMBOL_MULTIPLIER_NO_WIN_ALPHA : 1, this._otherMultiplierFadeInDuration, 1), 0);
                }
            }
        }

        if(showMultiplier) {
            timeline.add(otherMultiplierTimeline, timeline.totalDuration() - this._otherMultiplierFadeInDuration);
        }

        timeline.add(() => this.finish());

        this._timeline = timeline;
        this._timeline.play();

        if(Wixx.particles != null) {
            Wixx.particles.play(1.8);
        }
    }

    protected stopWinPresentation(isAborted:boolean):void {
        if(this._timeline) {
            if(this._timeline.isActive()) {
                this._timeline.pause();
                this._timeline.kill();
            }
            this._timeline = null;
        }
        while(this._winSymbols.length > 0) {
            const symbol = this._winSymbols.pop();
            if(symbol) {
                // symbol.replaceWith(`${symbol.symName}_boosted`);
            }
        }
        while(this._multiplierSymbols.length > 0) {
            const symbol:WixxReelSymbol = <WixxReelSymbol>this._multiplierSymbols.pop()!;
            symbol.playMultiplier(symbol.currentState!.keyword === WixxGameConfig.instance.SYMBOL_STATES.noWin!.keyword ? 0.3 : 1, 0, 1);
        }

        if(Wixx.particles != null) {
            Wixx.particles.play(1.8);
        }
    }
}